import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';
import { Mail, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

const EmailVerification = () => {
  const location = useLocation();
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resending, setResending] = useState(false);

  // Get email from URL params or localStorage
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get('email') || localStorage.getItem('verification_email') || '';

  const handleVerification = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setError('Please enter your verification code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('https://api.4fingerstudios.com/api/auth/verify-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          token: verificationCode
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Verification failed: ${response.status}`);
      }

      setSuccess('Email verified successfully! You can now log in to your account.');
      localStorage.removeItem('verification_email');
      
      // Redirect to login after a short delay
      setTimeout(() => {
        window.location.href = '/auth/login';
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError('Email address not found. Please try registering again.');
      return;
    }

    setResending(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('https://api.4fingerstudios.com/api/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to resend verification: ${response.status}`);
      }

      setSuccess('Verification code sent! Please check your email.');
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          <div className="mystical-card">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <Mail className="text-purple-800" size={48} />
              </div>
              <h1 className="mystical-title text-black mb-4">
                Verify Your Email
              </h1>
              <p className="mystical-text text-gray-600">
                We've sent a verification code to your email address. 
                Please enter the code below to complete your registration.
              </p>
              {email && (
                <p className="mystical-text text-sm text-purple-800 mt-2">
                  Code sent to: <strong>{email}</strong>
                </p>
              )}
            </div>

            {/* Success Message */}
            {success && (
              <div className="mystical-card bg-green-100 border border-green-400 text-green-700 mb-6">
                <div className="flex items-center space-x-2">
                  <CheckCircle size={20} />
                  <p className="mystical-text">{success}</p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mystical-card bg-red-100 border border-red-400 text-red-700 mb-6">
                <div className="flex items-center space-x-2">
                  <AlertCircle size={20} />
                  <p className="mystical-text">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleVerification} className="space-y-6">
              {/* Verification Code */}
              <div>
                <label className="block mystical-text text-black font-bold mb-2">
                  Verification Code *
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent text-center text-lg tracking-widest"
                  placeholder="Enter 6-digit code"
                  maxLength="6"
                  required
                />
                <p className="mystical-text text-sm text-gray-500 mt-2">
                  Check your email for the verification code. It may take a few minutes to arrive.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full mystical-button"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2" size={20} />
                    Verify Email
                  </>
                )}
              </button>
            </form>

            {/* Resend Code */}
            <div className="mt-6 text-center">
              <p className="mystical-text text-gray-600 mb-4">
                Didn't receive the code?
              </p>
              <button
                onClick={handleResendCode}
                disabled={resending}
                className="mystical-button bg-gray-600 hover:bg-gray-700"
              >
                {resending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2" size={16} />
                    Resend Code
                  </>
                )}
              </button>
            </div>

            {/* Help Information */}
            <div className="mt-8 mystical-card bg-white bg-opacity-95">
              <h3 className="mystical-subtitle text-black mb-3">Need Help?</h3>
              <ul className="mystical-text text-sm text-gray-600 space-y-2">
                <li>• Check your spam/junk folder</li>
                <li>• Make sure you entered the correct email address</li>
                <li>• The code expires after 24 hours</li>
                <li>• Contact support if you continue having issues</li>
              </ul>
            </div>

            {/* Navigation Links */}
            <div className="mt-8 text-center space-y-4">
              <Link to="/auth/login" className="mystical-button bg-gray-600 hover:bg-gray-700">
                Back to Login
              </Link>
              <Link to="/" className="mystical-button bg-gray-600 hover:bg-gray-700">
                Return Home
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EmailVerification;
